<template>
  <tr
    class="bg-white hover:bg-gray-50 transition-colors duration-150 ease-in-out text-right"
    @click="open = !open"
  >
    <td class="flex pl-3 py-5 items-center space-x-2 text-left">
      <button
        data-test="open-claim"
        type="button"
        class="btn-icon btn-icon-default"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-all duration-200"
          :class="[{'rotate-90': open}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
      </button>

      <template v-if="trade">
        <p class="w-60">
          {{ trade.name }}
        </p>
      </template>

      <template v-else>
        <p>Package</p>
      </template>
    </td>

    <td class="pr-3">
      {{ australianCurrency(totalCosts.budget) }}
    </td>

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.currentClaim) }}
    </td>

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.currentCertified) }}
    </td>

    <td
      class="bg-green-200/50"
      :class="{ 'text-red-600': hasVariance && variance !== 0 }"
      :title="hasVariance && variance !== 0 ? varianceComment?.body : ''"
    >
      {{ australianCurrency(totalCosts.currentClaim - totalCosts.currentCertified) }}
    </td>

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.previous) }}
    </td>

    <td class="bg-green-200/50 pr-3">
      {{ australianCurrency(totalCosts.cumulative) }}
    </td>

    <td class="bg-blue-100/50">
      <span v-if="showPercentages">
        {{ isNumber(totalCosts.currentClaim / totalCosts.budget) ? australianNumber((totalCosts.currentClaim / totalCosts.budget) * 100) : 0 }}%
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ isNumber(totalCosts.currentCertified / totalCosts.budget) ? australianNumber((totalCosts.currentCertified / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ isNumber(totalCosts.previous / totalCosts.budget) ? australianNumber((totalCosts.previous / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
    >
      {{ isNumber(totalCosts.cumulative / totalCosts.budget) ? australianNumber((totalCosts.cumulative / totalCosts.budget) * 100) : 0 }}%
    </td>

    <td class="pr-3">
      {{ australianCurrency(Math.abs(totalCosts.budget - totalCosts.cumulative)) }}
    </td>
  </tr>

  <template v-if="open">
    <LumpSumTradeRowItem
      v-for="tradeItem in rootTradeItems"
      :key="tradeItem.id"
      :can-edit-claim="canEditClaim"
      :trade="$props.trade"
      :claim="$props.claim"
      :trade-item="tradeItem"
      :package-trade-items="packageTradeItems"
      :show-percentages="$props.showPercentages"
    />
  </template>
</template>

<script setup lang="ts">
import { useSignedData } from '@/composables/useSignedData';
import { australianCurrency, australianNumber, falsey, isNumber } from '@/helpers';
import Claim, { ClaimStatus } from '@/models/Claim';
import PackageTradeItem from '@/models/PackageTradeItem';
import Trade from '@/models/Trade';
import TradeItem from '@/models/TradeItem';
import { useCommentsStore } from '@/store/comments';
import { useLumpSumCostsStore } from '@/store/lumpSumCosts';
import { useTradeItemsStore } from '@/store/tradeItems';
import { computed, ref, watch } from 'vue';
import LumpSumTradeRowItem from './LumpSumTradeRowItem.vue';

const props = defineProps<{
  trade?: Trade;
  claim: Claim;
  packageTradeItems: PackageTradeItem[];
  canEditClaim: boolean;
  showPercentages?: boolean;
}>();

const tradeItemsStore = useTradeItemsStore();

const getRootTradeItem = (tradeItem: TradeItem): TradeItem => {
  let current = tradeItemsStore.models.find(tradeItem.id) ?? tradeItem;

  while(current.parent_id) {
    const parent = tradeItemsStore.models.find(current.parent_id);

    if(!parent) {
      break;
    }

    current = parent;
  }

  return current;
};

const rootTradeItems = computed(() => {
  const roots = new Map<number, TradeItem>();

  [...props.packageTradeItems]
    .sort((a, b) => a.sort_order - b.sort_order)
    .forEach((packageTradeItem) => {
      if(!packageTradeItem.trade_item) {
        return;
      }

      const root = getRootTradeItem(packageTradeItem.trade_item);

      if(!roots.has(root.id)) {
        roots.set(root.id, root);
      }
    });

  return Array.from(roots.values());
});

const totalCosts = computed(() => {
  const totals = {
    budget: 0,
    currentClaim: 0,
    currentCertified: 0,
    previous: 0,
    cumulative: 0,
  };

  props.packageTradeItems.forEach((packageTradeItem) => {
    totals.budget += packageTradeItem.value;
    totals.previous += packageTradeItem.previous(props.claim);

    const lumpSumCost = packageTradeItem.lump_sum_costs[0];

    if(lumpSumCost) {
      totals.currentClaim += lumpSumCost.current_claim || 0;
      totals.currentCertified += lumpSumCost.current_certified || 0;
    }
  });

  totals.cumulative = totals.previous + totals.currentCertified;

  return totals;
});

const open = ref(false);
const commentsStore = useCommentsStore();

const varianceComment = computed(() => {
  if(props.trade) {
    return commentsStore.models
      .where('commentable_id', props.trade.id)
      .where('commentable_type', props.trade.$entity())
      .where('creator_id', props.claim.id)
      .where('creator_type', props.claim.$entity())
      .first();
  }

  return undefined;
});

const hasVariance = computed(() => {
  return !falsey(totalCosts.value?.currentClaim) && !falsey(totalCosts.value?.currentCertified);
});

const variance = computed(() => {
  return totalCosts.value?.currentClaim - totalCosts.value?.currentCertified;
});
</script>

<style scoped></style>
